import { get, set, trimEnd } from 'lodash';
import { EXPERIMENT_TOGGLE_POST_INDEX_SEO_SETTINGS } from '@wix/communities-blog-experiments';
import { POST_STATUS } from '../constants';
import { getMainCategoryDisplayLabel, getMainCategorySeoLabel, } from '../store/categories/categories-selectors';
import { isExperimentEnabled } from '../store/experiments/experiments-selectors';
import { getCanonicalPostUrl, getPostAmpUrl, getPostCover, getPostExcerpt, getPostSeoDescription, } from '../store/post/post-selectors';
import { capitalizeLanguageCode } from './capitalize-language-code';
import { getImageUrl } from './image-utils';
import { resolveId } from './resolve-id';
export const getFullLanguageCode = (siteLanguages = [], language = 'en') => {
    var _a;
    const langCode = ((_a = siteLanguages.find((siteLang) => siteLang.languageCode === language)) === null || _a === void 0 ? void 0 : _a.seoLang) || language;
    return capitalizeLanguageCode(langCode);
};
const getPostFlattenedCategories = (post, categoryMap) => post.categoryIds
    .map((id) => categoryMap[id])
    .filter(Boolean)
    .map(({ menuLabel }) => menuLabel)
    .join(', ');
export const mapTranslationData = (translation, siteLanguages) => {
    const langCode = getFullLanguageCode(siteLanguages, translation.language);
    return {
        langCode,
        url: `${translation.url.base}${translation.url.path}`,
        seoData: translation.seoData,
    };
};
const getTranslatedPagesData = (translations, siteLanguages = []) => translations
    .filter((translation) => translation.status === POST_STATUS.published)
    .map((translation) => mapTranslationData(translation, siteLanguages));
export const generatePostPageSEOTags = ({ appConfig, post, state, categoriesMap, customPostUrl, mediaUrls, multilingual, ownerUrl, }) => {
    var _a;
    const { postPageSectionUrl, ampBaseUrl, publisher } = appConfig;
    const { slug, seoDescription, title, seoTitle, lastPublishedDate, firstPublishedDate, owner = {}, translations = [], } = post;
    const mainCategory = (_a = getMainCategorySeoLabel(state, post)) !== null && _a !== void 0 ? _a : getMainCategoryDisplayLabel(state, post);
    const itemData = {
        post: {
            id: post.id,
            title,
            language: getFullLanguageCode(multilingual === null || multilingual === void 0 ? void 0 : multilingual.siteLanguages, post.language),
            description: getPostSeoDescription(post),
            amphtml: getPostAmpUrl(resolveId(post), ampBaseUrl, slug),
            type: 'article',
            canonicalUrl: getCanonicalPostUrl({
                post,
                postPageSectionUrl,
                customPostUrl,
                multilingual,
            }),
            optInMeta: 'max-snippet:-1, max-image-preview:large, max-video-preview:-1',
            slug,
            excerpt: getPostExcerpt(post),
            lastPublishedDate,
            firstPublishedDate,
            mainCategory,
            categories: getPostFlattenedCategories(post, categoriesMap, state),
            owner: {
                name: owner.name,
                url: ownerUrl,
            },
            path: customPostUrl || `${trimEnd(postPageSectionUrl, '/')}/${post.slug}`,
            contentImages: mediaUrls,
        },
        legacySeoData: {
            title: seoTitle,
            description: seoDescription,
        },
        postsSettings: {
            isAmpEnabled: Boolean(ampBaseUrl),
            isSchemaEnabled: true,
        },
        translatedPages: getTranslatedPagesData(translations, multilingual === null || multilingual === void 0 ? void 0 : multilingual.siteLanguages),
    };
    if (owner.image) {
        const ownerImage = Object.assign(Object.assign({}, owner.image), { file_name: owner.image.file_name || owner.image.id });
        set(itemData, 'post.owner.image.url', typeof owner.image === 'string'
            ? owner.image
            : getImageUrl({ image: ownerImage }));
    }
    if (publisher) {
        itemData.publisher = {
            id: publisher['@id'],
            name: publisher.name,
            logo: get(publisher, 'logo.url'),
        };
    }
    const isTogglePostIndexSEOExperimentEnabled = isExperimentEnabled(state, EXPERIMENT_TOGGLE_POST_INDEX_SEO_SETTINGS);
    const isPostShownInSearch = get(post, 'seoShowInSearch', true);
    if (isTogglePostIndexSEOExperimentEnabled) {
        if (!isPostShownInSearch) {
            itemData.post.noIndex = true;
        }
    }
    const { shouldRender, imageMetadata, videoMetadata } = getPostCover(post);
    if (shouldRender) {
        itemData.post.image = {
            url: imageMetadata
                ? getImageUrl({ image: imageMetadata })
                : videoMetadata.thumbnail_url,
            width: imageMetadata
                ? imageMetadata.width
                : videoMetadata.thumbnail_width,
            height: imageMetadata
                ? imageMetadata.height
                : videoMetadata.thumbnail_height,
        };
    }
    return itemData;
};
//# sourceMappingURL=generate-seo-tags.js.map